import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { getAuthToken } from "@/lib/sm-api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PackType } from "@/lib/pack-economy";

interface CatalogPack {
  id: string;
  packType: PackType;
  name: string;
  description: string;
  quantity: number;
  priceArs: number | null;
  priceUsd: number | null;
  validityDays: number | null;
  active: boolean;
  featured: boolean;
  recommended: boolean;
  sortOrder: number;
}

type PackStats = { sold: number; granted: number; remaining: number; revenueCents: number; tenants: number };

const PACK_TYPES: PackType[] = [
  "email",
  "order",
  "push",
  "storage",
  "image",
  "calendar",
  "b2b",
  "payway",
  "api",
  "bandwidth",
  "booking",
  "team_member",
  "mcp",
];

const EMPTY_FORM: CatalogPack = {
  id: "",
  packType: "order",
  name: "",
  description: "",
  quantity: 100,
  priceArs: null,
  priceUsd: null,
  validityDays: 30,
  active: true,
  featured: false,
  recommended: false,
  sortOrder: 0,
};

export function AdminPacksPanel() {
  const [catalog, setCatalog] = useState<CatalogPack[]>([]);
  const [stats, setStats] = useState<Record<string, PackStats>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CatalogPack>(EMPTY_FORM);
  const [editing, setEditing] = useState(false);

  const authHeaders = (): HeadersInit => ({
    "Content-Type": "application/json",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/packs", { credentials: "include", headers: authHeaders() });
      if (r.ok) {
        const d = (await r.json()) as { catalog?: CatalogPack[]; stats?: Record<string, PackStats> };
        setCatalog(d.catalog ?? []);
        setStats(d.stats ?? {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!form.id.trim() || !form.name.trim()) return;
    setSaving(true);
    try {
      const r = await fetch("/api/admin/packs", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (r.ok) {
        setForm(EMPTY_FORM);
        setEditing(false);
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: string) => {
    await fetch(`/api/admin/packs?id=${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    await load();
  };

  const startEdit = (p: CatalogPack) => {
    setForm(p);
    setEditing(true);
  };

  const groupedByType = useMemo(() => {
    const groups = new Map<PackType, CatalogPack[]>();
    for (const p of catalog) {
      if (!groups.has(p.packType)) groups.set(p.packType, []);
      groups.get(p.packType)!.push(p);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [catalog]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{editing ? "Editar pack" : "Nuevo pack"}</p>
          {editing ? (
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setForm(EMPTY_FORM); }}>
              Cancelar
            </Button>
          ) : null}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <Label className="text-xs">ID (slug)</Label>
            <Input
              className="mt-1"
              value={form.id}
              disabled={editing}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
              placeholder="pack-order-500"
            />
          </div>
          <div>
            <Label className="text-xs">Nombre</Label>
            <Input className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={form.packType} onValueChange={(v) => setForm((f) => ({ ...f, packType: v as PackType }))}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PACK_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Cantidad</Label>
            <Input
              type="number"
              className="mt-1"
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) }))}
            />
          </div>
          <div>
            <Label className="text-xs">Precio ARS</Label>
            <Input
              type="number"
              className="mt-1"
              value={form.priceArs ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, priceArs: e.target.value ? Number(e.target.value) : null }))}
            />
          </div>
          <div>
            <Label className="text-xs">Validez (días)</Label>
            <Input
              type="number"
              className="mt-1"
              value={form.validityDays ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, validityDays: e.target.value ? Number(e.target.value) : null }))}
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Label className="text-xs">Descripción</Label>
            <Input className="mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
        </div>
        <Button size="sm" disabled={saving} onClick={() => void save()}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {editing ? "Guardar cambios" : "Crear pack"}
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Catálogo ({catalog.length})</p>
        {catalog.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin packs (¿migración 019 aplicada?).</p>
        ) : (
          <div className="space-y-4">
            {groupedByType.map(([packType, packs]) => (
              <div key={packType} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {packType} <span className="font-normal normal-case">({packs.length})</span>
                </h3>
                {packs.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-sm">{p.name}</p>
                        {!p.active ? <Badge variant="secondary">inactivo</Badge> : null}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {p.id} · {p.quantity} u · ${p.priceArs?.toLocaleString("es-AR") ?? "—"} ARS
                        {stats[p.id]
                          ? ` · ${stats[p.id].sold} vendidos · ${stats[p.id].remaining} restantes · ${stats[p.id].tenants} tenants · $${Math.round(stats[p.id].revenueCents / 100).toLocaleString("es-AR")}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="secondary" onClick={() => startEdit(p)}>Editar</Button>
                      {p.active ? (
                        <Button size="sm" variant="ghost" onClick={() => void deactivate(p.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
